import { Injectable } from '@angular/core';
import { DefaultEditOptions, AllCodesDuplicationQuery, EditOptions, ProductDto, ProductEditOptions, ProductPageResult, ProductQuery, AllRxsDuplicationQuery, ProductImportDto, RelatedRxForSyncDto, RelatedProductForSyncDto, RxStockShortageDto, RxStockBatchResultDto, ProductBatchModifyDto, ProductBatchModifyResultDto, LoginStockAlertDto } from 'src/app/stocks/product/models/product';
import { WebApiClient } from '../../web-api-client.service';
import { WebApiService } from '../../web-api.service';
import { ValueTextPair } from 'src/app/shared/models/value-text-pair';
import { BaseConfig } from '../../base-config.service';
import { HRxDTO } from 'src/app/hist/models/hrx-calcr.models';

@Injectable({ providedIn: 'root' })
export class ProductApi {
  private apiService: WebApiService;
  editOptions: ProductEditOptions = DefaultEditOptions;

  constructor(private apiClient: WebApiClient) {
    this.apiService = this.apiClient.createHisService('Stocks/Product');
  }

  GetAll(query: ProductQuery) {
    const payload: ProductQuery = {
      // 缺欄位送空字串, 避免 undefined 被序列化成字串 "undefined" 而被後端當成篩選值
      productCode: query.productCode?.trim() ?? '',
      barcode: query.barcode?.trim() ?? '',
      productName: query.productName?.trim() ?? '',
      vendorCode: query.vendorCode?.trim() ?? '',
      // 不傳(undefined)時後端不篩選, 維持其他呼叫端原行為
      isUseBatchNo: query.isUseBatchNo,
      pageSkip: query.pageSkip ?? 0,
      pageSize: query.pageSize ?? BaseConfig.getConfig().ui.pageSize
    };
    return this.apiService.get('GetAll', payload).toPromise<ProductPageResult>();
  }

  Get(id: number, isShowLoader = true) {
    return this.apiService.get(`Get/${id}`, null, isShowLoader).toPromise<ProductDto>();
  }

  Create(model: ProductDto) {
    return this.apiService.post('Create', model).toPromise<ProductDto>();
  }

  Update(model: ProductDto, syncRxIds?: number[]) {
    let url = 'Update';
    if (syncRxIds && syncRxIds.length > 0) {
      const params = syncRxIds.map(id => `syncRxIds=${encodeURIComponent(id)}`).join('&');
      url += '?' + params;
    }
    return this.apiService.put(url, model).toPromise<ProductDto>();
  }

  async BatchModify(dto: ProductBatchModifyDto): Promise<ProductBatchModifyResultDto> {
    return await this.apiService.put('BatchModify', dto).toPromise<ProductBatchModifyResultDto>();
  }

  async GetRelatedRxListForPriceSync(productId: number): Promise<RelatedRxForSyncDto[]> {
    const result = await this.apiService.get('GetRelatedRxListForPriceSync', { productId: productId }).toPromise<RelatedRxForSyncDto[]>();
    return result ?? [];
  }

  async GetRelatedProductForPriceSync(rxId: number): Promise<RelatedProductForSyncDto | null> {
    const result = await this.apiService.get('GetRelatedProductForPriceSync', { rxId: rxId }).toPromise<RelatedProductForSyncDto | null>();
    return result ?? null;
  }

  Delete(id: number) {
    return this.apiService.delete(`Delete/${id}`).toPromise<void>();
  }

  async PreviewProductCode(): Promise<string> {
    const result = await this.apiService.get('PreviewProductCode').toPromise<string>();
    return result ?? '';
  }

  async GetEditOptions() {
    const options = await this.apiService.get('GetEditOptions').toPromise<EditOptions>();
    this.editOptions = {
      ...DefaultEditOptions,
      ...(options ?? {}),
      vendors: this.AddEmptyOption(options?.vendors),
      productDepartments: this.AddEmptyOption(options?.productDepartments),
      productCategories: this.AddEmptyOption(options?.productCategories),
      productCombines: this.AddEmptyOption(options?.productCombines),
      productCombinePrices: this.AddEmptyOption(options?.productCombinePrices),
      units: this.AddEmptyOption(options?.units),
    };
    return this.editOptions;
  }

  AddEmptyOption(source?: ValueTextPair[]): ValueTextPair[] {
    if (!source || source.length === 0) {
      return [{ value: '', text: '' }];
    }
    return [{ value: '', text: '' }, ...source];
  }

  async GetHasProduct(): Promise<boolean> {
    return await this.apiService.get(`GetHasProduct`).toPromise<boolean>();
  } 

  async ValidateAllCodesDuplication(query: AllCodesDuplicationQuery): Promise<void> {
    return await this.apiService.get(`ValidateAllCodesDuplication`, query).toPromise<void>();
  }

  async ValidateAllRxsDuplication(query: AllRxsDuplicationQuery): Promise<void> {
    return await this.apiService.get(`ValidateAllRxsDuplication`, query).toPromise<void>();
  }

  async CreateProductImport(data: ProductImportDto): Promise<[string, string, number]> {
    return await this.apiService.post('CreateProductImport', data, false).toPromise<[string, string, number]>();
  }

  async CreateProductRxImport(data: ProductImportDto): Promise<number> {
    return await this.apiService.post('CreateProductRxImport', data, false).toPromise<number>();
  }

  async CheckLowStockInventory(): Promise<boolean> {
    return await this.apiService.get('CheckLowStockInventory').toPromise<boolean>();
  }

  /** 登入庫存警示檢查 (庫存不足 + 批號效期) */
  async CheckStockAlert(): Promise<LoginStockAlertDto> {
    return await this.apiService.get('CheckStockAlert').toPromise<LoginStockAlertDto>();
  }

  async CheckProductHasBatchData(productId: number): Promise<boolean> {
    return await this.apiService.get(`CheckProductHasBatchData/${productId}`).toPromise<boolean>();
  }

  async CheckRxStock(data: HRxDTO): Promise<[boolean, RxStockShortageDto[]]> {
    return await this.apiService.post('CheckRxStock', data).toPromise<[boolean, RxStockShortageDto[]]>();
  }

  async CheckRxStockBatch(data: HRxDTO[]): Promise<RxStockBatchResultDto[]> {
    return await this.apiService.post('CheckRxStockBatch', data).toPromise<RxStockBatchResultDto[]>();
  }

  async GetHeadquartersStockParamAsync(clinicId: number): Promise<{EnableStockSystem:boolean,EnableHeadquarterRx:boolean,EnableRealtimeStock:boolean}> {
    return await this.apiService.get('GetHeadquartersStockParamAsync', { clinicId: clinicId }).toPromise();
  }
  async CheckHeadquartersStockParam(clinicId: number): Promise<boolean> {
    return await this.apiService.get('CheckHeadquartersStockParam', { clinicId: clinicId }).toPromise<boolean>();
  }

  async CheckHeadquartersRxParam(clinicId: number): Promise<boolean> {
    return await this.apiService.get('CheckHeadquartersRxParam', { clinicId: clinicId }).toPromise<boolean>();
  }

  async CheckHeadquartersRealtimeParam(clinicId: number): Promise<boolean> {
    return await this.apiService.get('CheckHeadquartersRealtimeParam', { clinicId: clinicId }).toPromise<boolean>();
  }
}
